import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Snackbar,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Notes as NotesIcon,
  PostAdd as PostAddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { romaneioService, Romaneio, ProdutoRomaneio } from '../../services/romaneio';
import { produtoService, Produto } from '../../services/produto';

interface RomaneioFormData {
  id?: string;
  idFuncionario: number | null;
  idPraca: number | null;
  dataSaida: string;
  dataRetorno: string;
  finalizado: boolean;
  produtos: ProdutoRomaneio[];
}

const RomaneioForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const isEditing = Boolean(id);
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<RomaneioFormData>({
    idFuncionario: null,
    idPraca: null,
    dataSaida: '',
    dataRetorno: '',
    finalizado: true,
    produtos: [],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProduto, setSelectedProduto] = useState<ProdutoRomaneio | null>(null);
  const [codigoBarrasText, setCodigoBarrasText] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  // Estado separado para controlar o valor do Autocomplete
  const [autocompleteValues, setAutocompleteValues] = useState<{[key: string]: any}>({});
  // Controla o texto digitado no campo de produto para filtrar as opções
  const [produtoInputs, setProdutoInputs] = useState<{[key: number]: string}>({});

  // Buscar dados do romaneio para edição
  const { data: romaneioData, isLoading: romaneioLoading } = useQuery({
    queryKey: ['romaneio', id],
    queryFn: () => romaneioService.getById(id!),
    enabled: isEditing && !!id,
  });

  // Buscar produtos disponíveis para seleção
  const { data: produtosDisponiveis, isLoading: produtosLoading } = useQuery<Produto[]>({
    queryKey: ['produtos', 'all'],
    queryFn: () => produtoService.getAll(), // Usar getAll para buscar todos produtos
  });

  // Debug para verificar produtos carregados
  console.log('Produtos disponíveis:', produtosDisponiveis);
  console.log('Loading produtos:', produtosLoading);

  // Carregar dados do romaneio quando estiver editando
  useEffect(() => {
    console.log('Dados do romaneio recebidos (edição):', romaneioData);
    if (isEditing && romaneioData) {
      setFormData({
        id: romaneioData.id,
        idFuncionario: romaneioData.idFuncionario ? parseInt(romaneioData.idFuncionario) : null,
        idPraca: romaneioData.idPraca ? parseInt(romaneioData.idPraca) : null,
        dataSaida: romaneioData.dataSaida ? romaneioData.dataSaida.split('T')[0] : '',
        dataRetorno: romaneioData.dataRetorno ? romaneioData.dataRetorno.split('T')[0] : '',
        finalizado: romaneioData.finalizado || false,
        produtos: romaneioData.produtos || [],
      });
    }
  }, [isEditing, romaneioData]);

  // Sincronizar valores do Autocomplete com o formData
  useEffect(() => {
    const newValues: {[key: string]: any} = {};
    formData.produtos.forEach((produto, index) => {
      if (produto.idProduto && produtosDisponiveis) {
        const found = produtosDisponiveis.find(p => p.id === parseInt(produto.idProduto));
        newValues[`produto-${index}`] = found || null;
      } else {
        newValues[`produto-${index}`] = null;
      }
    });
    setAutocompleteValues(newValues);
  }, [formData.produtos, produtosDisponiveis]);

  // Mutation para salvar
  const saveMutation = useMutation({
    mutationFn: (data: RomaneioFormData) => {
      const produtosAgrupados = data.produtos.reduce<ProdutoRomaneio[]>((acc, produto) => {
        const idProduto = produto.idProduto ? produto.idProduto.toString().trim() : '';
        if (!idProduto) {
          acc.push({ ...produto });
          return acc;
        }

        const existente = acc.find(p => p.idProduto === idProduto);
        if (existente) {
          existente.quantidade =
            (Number(existente.quantidade) || 0) + (Number(produto.quantidade) || 0);
        } else {
          acc.push({ ...produto });
        }

        return acc;
      }, []);

      const romaneioToSave = {
        id: data.id || '0',
        idFuncionario: data.idFuncionario?.toString() || '',
        idPraca: data.idPraca?.toString() || '',
        dataSaida: data.dataSaida,
        dataRetorno: data.dataRetorno,
        finalizado: data.finalizado,
        produtos: produtosAgrupados.map(produto => ({
          id: produto.id || '0',
          idRomaneio: data.id || '0',
          CodProduto: produto.idProduto || '',
          quantidade: parseInt(produto.quantidade.toString()) || 0,
          idSacola: produto.idSacola ? produto.idSacola.toString() : null,
          nomeProduto: produto.nomeProduto || '',
          precoVenda: produto.precoVenda || '0.00',
          referencia: produto.referencia || '',
          quantidadeDisponivel: produto.quantidadeDisponivel || 0,
          codigoBarras: [],
        })) as any,
      };
      console.log('Objeto sendo salvo:', romaneioToSave);
      return romaneioService.salvar(romaneioToSave);
    },
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: ['romaneios'] });
      queryClient.refetchQueries({ queryKey: ['produtos'] });
      queryClient.refetchQueries({ queryKey: ['produtos', 'select'] });
      navigate('/romaneio');
    },
    onError: (error: any) => {
      console.error('Erro ao salvar romaneio:', error);
      setErrors({ 
        submit: error.response?.data?.message || 'Erro ao salvar romaneio' 
      });
    },
  });

  const handleInputChange = (field: keyof RomaneioFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Limpar erro do campo quando usuário digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleProdutoChange = (index: number, field: keyof ProdutoRomaneio, value: any) => {
    setFormData(prev => ({
      ...prev,
      produtos: prev.produtos.map((produto, i) => {
        if (i === index) {
          const updatedProduto = { ...produto, [field]: value };
          
          // Se mudou o idProduto, preencher dados automaticamente
          if (field === 'idProduto' && value) {
            const produtoSelecionado = produtosDisponiveis?.find(p => p.id === parseInt(value));
            if (produtoSelecionado) {
              updatedProduto.nomeProduto = produtoSelecionado.nomeProduto;
              updatedProduto.precoVenda = produtoSelecionado.precoVenda.toString();
              updatedProduto.referencia = produtoSelecionado.referencia;
              updatedProduto.quantidadeDisponivel = produtoSelecionado.quantidadeDisponível || 0;
            }
          }
          
          return updatedProduto;
        }
        return produto;
      }),
    }));
  };

  const handleProdutoInputChange = (index: number, value: string) => {
    setProdutoInputs(prev => ({
      ...prev,
      [index]: value,
    }));
  };

  const handleAddProduto = () => {
    const novoProduto: ProdutoRomaneio = {
      id: '0',
      idRomaneio: '',
      idProduto: '',
      quantidade: 1,
      precoVenda: '0.00',
      referencia: '',
      quantidadeDisponivel: undefined,
      idSacola: undefined,
    };
    setFormData(prev => ({
      ...prev,
      produtos: [...prev.produtos, novoProduto],
    }));
  };

  const handleRemoveProduto = (index: number) => {
    setFormData(prev => ({
      ...prev,
      produtos: prev.produtos.filter((_, i) => i !== index),
    }));
  };

  const handleOpenCodigoBarras = (produto: ProdutoRomaneio) => {
    setSelectedProduto(produto);
    setCodigoBarrasText(produto.codigoBarras || '');
    setDialogOpen(true);
  };

  const handleSaveCodigoBarras = () => {
    if (selectedProduto) {
      const codigosArray = codigoBarrasText.split('\n').filter(c => c.trim());
      if (codigosArray.length === selectedProduto.quantidade) {
        handleProdutoChange(
          formData.produtos.findIndex(p => p.id === selectedProduto.id),
          'codigoBarras',
          codigoBarrasText
        );
        setDialogOpen(false);
        setSelectedProduto(null);
        setCodigoBarrasText('');
      } else {
        alert(`Quantidade informada (${selectedProduto.quantidade}) não corresponde ao número de códigos (${codigosArray.length})`);
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.idFuncionario) {
      newErrors.idFuncionario = 'Funcionário é obrigatório';
    }

    if (!formData.idPraca) {
      newErrors.idPraca = 'Praça é obrigatória';
    }

    if (!formData.dataSaida) {
      newErrors.dataSaida = 'Data de saída é obrigatória';
    }

    if (!formData.dataRetorno) {
      newErrors.dataRetorno = 'Data de retorno é obrigatória';
    }

    if (formData.produtos.length === 0) {
      newErrors.produtos = 'Adicione pelo menos um produto';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      saveMutation.mutate(formData);
    }
  };

  const handleBack = () => {
    navigate('/romaneio');
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (romaneioLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} color="primary">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {isEditing ? `Editar Romaneio #${id}` : 'Novo Romaneio'}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            {/* Funcionário */}
            <Box>
              <TextField
                fullWidth
                label="Funcionário"
                value={
                  isEditing
                    ? (romaneioData?.nomePromotor || romaneioData?.funcionario || formData.idFuncionario?.toString() || '')
                    : (formData.idFuncionario?.toString() || '')
                }
                onChange={(e) => handleInputChange('idFuncionario', parseInt(e.target.value) || null)}
                error={!!errors.idFuncionario}
                helperText={errors.idFuncionario}
                disabled={isEditing}
              />
            </Box>

            {/* Praça */}
            <Box>
              <TextField
                fullWidth
                label="Praça"
                value={
                  isEditing
                    ? (romaneioData?.nomePraca || romaneioData?.praca || formData.idPraca?.toString() || '')
                    : (formData.idPraca?.toString() || '')
                }
                onChange={(e) => handleInputChange('idPraca', parseInt(e.target.value) || null)}
                error={!!errors.idPraca}
                helperText={errors.idPraca}
                disabled={isEditing}
              />
            </Box>

            {/* Data de Saída */}
            <Box>
              <TextField
                fullWidth
                label="Data de Saída"
                type="date"
                value={formData.dataSaida}
                onChange={(e) => handleInputChange('dataSaida', e.target.value)}
                error={!!errors.dataSaida}
                helperText={errors.dataSaida}
                slotProps={{
                  htmlInput: { shrink: true }
                }}
              />
            </Box>

            {/* Data de Retorno */}
            <Box>
              <TextField
                fullWidth
                label="Data de Retorno"
                type="date"
                value={formData.dataRetorno}
                onChange={(e) => handleInputChange('dataRetorno', e.target.value)}
                error={!!errors.dataRetorno}
                helperText={errors.dataRetorno}
                slotProps={{
                  htmlInput: { shrink: true }
                }}
              />
            </Box>

            {/* Status */}
            <Box sx={{ gridColumn: '1 / -1' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.finalizado}
                    onChange={(e) => handleInputChange('finalizado', e.target.checked)}
                  />
                }
                label={`Status: ${formData.finalizado ? 'FINALIZADO' : 'INICIADO'}`}
              />
            </Box>
          </Box>

          {/* Produtos */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Produtos do Romaneio
            </Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>Produto</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Preço</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Qtd. Disponível</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Nº Sacola</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.produtos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography sx={{ py: 4, color: 'text.secondary' }}>
                          Nenhum produto adicionado. Clique no botão abaixo para adicionar.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    formData.produtos.map((produto, index) => (
                      <TableRow key={index}>
                        <TableCell>
                              {produto.id === '0' ? (
                                <Autocomplete
                                  fullWidth
                                  size="small"
                                  options={(() => {
                                    const input = (produtoInputs[index] ?? produto.referencia ?? '').toString().trim().toLowerCase();
                                    if (!input || !produtosDisponiveis) return produtosDisponiveis || [];
                                    return produtosDisponiveis.filter((o) => {
                                      const ref = (o.referencia || '').toString().toLowerCase();
                                      const nome = (o.nomeProduto || '').toLowerCase();
                                      return ref === input || nome.startsWith(input);
                                    });
                                  })()}
                                  getOptionLabel={(option: Produto) => `${option.referencia || ''} - ${option.nomeProduto}`}
                                  value={produto.idProduto ? (produtosDisponiveis || []).find(p => p.id === Number(produto.idProduto)) || null : null}
                                  getOptionKey={(option: Produto) => option.id}
                                  isOptionEqualToValue={(option: Produto, value: Produto) => option.id === value.id}
                                  filterOptions={(options) => [...options]}
                                  onChange={(_, newValue: Produto | null) => {
                                    console.log('Autocomplete onChange - index:', index, 'newValue:', newValue);
                                    console.log('Produto ANTES da atualização:', formData.produtos[index]);

                                    if (newValue) {
                                      console.log('newValue completo:', JSON.stringify(newValue, null, 2));
                                      console.log('newValue.id:', newValue.id, 'typeof newValue.id:', typeof newValue.id);
                                      console.log('newValue.id.toString():', newValue.id.toString());

                                      const updatedProdutos = formData.produtos.map((produto, i) => {
                                        if (i === index) {
                                          const updatedProduto = {
                                            ...produto,
                                            idProduto: newValue.id.toString(),
                                            nomeProduto: newValue.nomeProduto,
                                            precoVenda: newValue.precoVenda.toString(),
                                            referencia: newValue.referencia,
                                          };
                                          console.log('Produto DEPOIS da atualização:', updatedProduto);
                                          return updatedProduto;
                                        }
                                        return produto;
                                      });
                                      
                                      setFormData(prev => ({
                                        ...prev,
                                        produtos: updatedProdutos,
                                      }));
                                      setProdutoInputs(prev => ({
                                        ...prev,
                                        [index]: '',
                                      }));
                                    } else {
                                      console.log('Autocomplete onChange - newValue é null, limpando produto index:', index);
                                      setFormData(prev => ({
                                        ...prev,
                                        produtos: prev.produtos.map((produto, i) => {
                                          if (i === index) {
                                            return {
                                              ...produto,
                                              idProduto: '',
                                              nomeProduto: '',
                                              precoVenda: '0.00',
                                              referencia: '',
                                              quantidadeDisponivel: 0,
                                            };
                                          }
                                          return produto;
                                        }),
                                      }));
                                      setProdutoInputs(prev => ({
                                        ...prev,
                                        [index]: '',
                                      }));
                                    }
                                  }}
                                  renderInput={(params) => {
                                    const digitado = produtoInputs[index]?.toString().trim();
                                    const selected = produto.idProduto ? (produtosDisponiveis || []).find(p => p.id === Number(produto.idProduto)) : null;
                                    const inputValue = digitado
                                      ? produtoInputs[index].toString()
                                      : (selected ? `${selected.referencia || ''} - ${selected.nomeProduto || ''}` : produto.referencia || '');
                                    return (
                                      <TextField
                                        {...params}
                                        slotProps={{
                                          ...params.slotProps,
                                          htmlInput: {
                                            ...(params.slotProps?.htmlInput || {}),
                                            value: inputValue,
                                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleProdutoInputChange(index, e.target.value),
                                          },
                                        }}
                                        label="Produto"
                                        placeholder="Selecione um produto..."
                                      />
                                    );
                                  }}
                                />
                              ) : (
                                produto.nomeProduto || '-'
                              )}
                            </TableCell>
                        <TableCell>{produto.precoVenda || '-'}</TableCell>
                        <TableCell>{produto.referencia || '-'}</TableCell>
                        <TableCell>{produto.quantidadeDisponivel || '-'}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={produto.quantidade}
                            onChange={(e) => handleProdutoChange(index, 'quantidade', parseInt(e.target.value) || 0)}
                            disabled={produto.id !== '0'}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={produto.idSacola || ''}
                            onChange={(e) => handleProdutoChange(index, 'idSacola', e.target.value || null)}
                          disabled={produto.id !== '0'}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                    <TableCell>
                      {produto.id === '0' && (
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveProduto(index)}
                          title="Remover produto"
                          sx={{ color: 'error.main' }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

            {errors.produtos && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors.produtos}
              </Alert>
            )}

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                onClick={handleAddProduto}
                sx={{ mr: 2 }}
              >
                Adicionar Produto
              </Button>
            </Box>
          </Box>

          {/* Erro geral */}
          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <Alert severity="error">
                {errors.submit}
              </Alert>
            </Box>
          )}

          {/* Botões de ação */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              disabled={saveMutation.isPending}
            >
              Voltar
            </Button>
            <Button
              variant="contained"
              type="submit"
              startIcon={<SaveIcon />}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Modal para códigos de barras */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Código de Barras</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={15}
            label="Código de barras"
            value={codigoBarrasText}
            onChange={(e) => setCodigoBarrasText(e.target.value)}
            placeholder="Digite um código de barras por linha"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSaveCodigoBarras} variant="contained">
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para mensagens */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
      />
    </Box>
  );
};

export default RomaneioForm;
